/**
 * Resource registry for the Business (Organisation Admin) module.
 *
 * Every entry describes a single REST resource that the org admin can
 * manage: which API client to use, the columns to render in the list,
 * the fields to render in the create/edit form, and how to look up the
 * dropdown choices (loaders) when an FK field is involved.
 *
 * Adding a new resource = add an entry here.  No new pages required.
 */
import { BusinessAPI, SuperAdminAPI, UsersAPI, PermissionGroupAPI } from '../../lib/api';
import { isSuperOnPublicSchema } from '../../config';

// Common helpers ------------------------------------------------------------

/** Loader for Permission user group <select> options. */
const loadPermissionGroups = () =>
  PermissionGroupAPI.list({ page_size: 500 }).then((d) => d.results || d);

/** Loader for User <select> options. */
const loadUsers = () =>
  UsersAPI.list({ page_size: 500 }).then((d) => d.results || d);

/** Loader for Organisation <select> options. */
const loadOrgs = () =>
  SuperAdminAPI.organisations.list({ page_size: 500 }).then((d) => d.results || d);

/** Loader for Division <select> options. */
const loadDivisions = () =>
  BusinessAPI.divisions.list({ page_size: 500 }).then((d) => d.results || d);

/**
 * Loader for SubDivision <select> options.
 *
 * Returns EVERY sub-division in the org rather than only those under the
 * currently-picked division: ``ResourceForm`` loads each field's options
 * once on mount and has no dependent-dropdown mechanism, so filtering by
 * the sibling ``division`` field would require the loader to re-run on
 * change — which it doesn't.  The label therefore carries the parent
 * division name ("Customer Care (Auretics)") so the admin can still tell
 * same-named sub-divisions apart.
 *
 * The backend is the safety net for a mismatched pair: if the chosen
 * sub-division doesn't belong to the chosen division,
 * ``external_complaint_defaults`` ignores the sub-division rather than
 * filing a contradictory ticket.
 */
const loadSubDivisions = () =>
  BusinessAPI.subDivisions.list({ page_size: 500 }).then((d) => d.results || d);


/** Loader for Role <select> options. */
const loadRoles = () =>
  SuperAdminAPI.roles.list({ page_size: 500 }).then((d) => d.results || d);

/** Loader for OrganisationalHierarchy <select> options. */
const loadHierarchies = () =>
  SuperAdminAPI.hierarchies.list({ page_size: 500 }).then((d) => d.results || d);


/** Standard columns used by most resources that have a `name` + audit. */
const NAME_COLUMNS = [
  { key: 'id',         label: 'ID' },
  { key: 'name',       label: 'Name' },
  { key: 'created_on', label: 'Created on', format: 'date' },
];

/** Owner column add-on (renders the owner email if present). */
const OWNER_COLUMN = { key: 'owner_email', label: 'Owner' };

// Resource definitions ------------------------------------------------------
export const BUSINESS_RESOURCES = {
  // ----- Org-scoped Users CRUD -----------------------------------
  //
  // Mounted under /business/users so an organisation admin can add /
  // edit / remove users for THEIR OWN organisation directly from the
  // tenant subdomain (e.g. ``acme.tasky.do/business/users``) — no
  // detour through the platform-operator super-admin host.
  //
  // Backend semantics (see ``super_admin/api_views.UserViewSet``):
  //
  //   * The viewset is mounted in the tenant URLConf
  //     (``taskydo/urls_tenant.py``) at ``/api/super_admin/users/``.
  //     ``TenantAwareJWTAuthentication`` rejects tokens whose
  //     ``schema_name`` claim doesn't match the request's resolved
  //     tenant, and ``TenantUserBindingMiddleware`` rejects users
  //     whose ``organisation_id`` doesn't match the request's tenant.
  //     Together they guarantee that a tenant-side caller (the org
  //     admin) can only ever read / write rows for their own org.
  //
  //   * ``UserViewSet.create`` defaults the ``organisation`` FK to the
  //     caller's own ``organisation_id`` when the field is omitted
  //     (which is exactly what the form below does — there is no
  //     organisation picker, by design).  A defense-in-depth check
  //     further down the same view rejects any cross-org POST with a
  //     403, so a tampered request body cannot leak users into a
  //     different tenant either.
  //
  //   * ``UserViewSet.list`` runs ``_filter_by_org`` so the listing
  //     only contains users from the caller's own organisation.
  //
  // Net effect: this entry adds a fully-functioning "Add user" /
  // "Edit user" / "Change password" surface that is automatically
  // scoped to the org, with no extra org guard required in the
  // frontend itself.
  users: {
    slug: 'users',
    label: 'Users',
    singular: 'User',
    icon: '👤',
    api: SuperAdminAPI.users,
    // Super-admin on the public host must not aggregate every tenant
    // (that is the Super Admin → Users screen).  Business → Users here
    // is the public organisation's people.
    listParams: (user) => (
      isSuperOnPublicSchema(user) ? { scope: 'public-org' } : {}
    ),
    columns: [
      { key: 'id',           label: 'ID' },
      { key: 'email',        label: 'Email' },
      { key: 'full_name',    label: 'Name' },
      { key: 'role_name',    label: 'Role' },
      { key: 'is_active',    label: 'Active', format: 'bool' },
      { key: 'is_admin',     label: 'Org admin',  format: 'bool',
        hideIf: (user) => isSuperOnPublicSchema(user) },
      { key: 'is_complaints_admin', label: 'Complaints admin', format: 'bool' },
    ],
    // NOTE: NO ``organisation`` field.  The backend defaults it to the
    // requesting org admin's own organisation_id (see
    // ``UserViewSet.create``).  Exposing the picker here would only
    // give a malicious admin a UI to attempt cross-org writes — which
    // the backend would reject with a 403 anyway, but the cleanest
    // story is "no field, no temptation".
    fields: [
      // ----- IDENTITY ------------------------------------------------
      { name: 'first_name',   label: 'First name', type: 'text' },
      { name: 'last_name',    label: 'Last name',  type: 'text' },
      { name: 'email',        label: 'Email',      type: 'email', required: true },
      { name: 'username',     label: 'Username',   type: 'text' },
      { name: 'phone_number', label: 'Phone',      type: 'tel' },

      // ----- ORG STRUCTURE (loaders read tenant-scoped tables) -------
      // ``Role`` / ``OrganisationalHierarchy`` / supervisor / hr all
      // live in the tenant schema, so the loaders return only this
      // org's rows automatically.  When the org has no Roles /
      // Hierarchies yet (the scenario in the brief) the dropdowns
      // simply render with the "— Select —" placeholder; the form
      // serialises empty optional FKs out of the payload via
      // ResourceForm's empty-string filter, so the create still
      // succeeds.
      { name: 'role',         label: 'Role',       type: 'select',
        loader: loadRoles,        optionLabel: (r) => r.name },
      { name: 'permission_user_group', label: 'Permission group', type: 'select',
        loader: loadPermissionGroups, optionLabel: (g) => g.name,
        help: 'When set, module access is inherited from the group and individual permissions are locked.' },
      { name: 'hierarchy',    label: 'Hierarchy',  type: 'select',
        loader: loadHierarchies,  optionLabel: (h) => h.name },
      { name: 'supervisor',   label: 'Supervisor', type: 'select',
        loader: loadUsers,
        optionLabel: (u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`.trim() },
      { name: 'hr',           label: 'HR',         type: 'select',
        loader: loadUsers,
        optionLabel: (u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`.trim() },

      // ----- COMPLAINT ROUTING (Auretics portal users) ---------------
      // These two decide WHERE an external user's complaint lands.
      // ``external_complaint_defaults`` reads the submitter's own
      // ``division`` / ``sub_division`` first and only falls back to the
      // "Auretics" division when they're unset — so leaving these blank
      // sends every portal ticket to the same catch-all queue, which is
      // the behaviour the org admin is trying to escape.  Exposing them
      // here is what makes per-user routing configurable without a
      // shell.
      { name: 'division',     label: 'Division (complaint routing)',
        type: 'select', loader: loadDivisions, optionLabel: (d) => d.name },
      { name: 'sub_division', label: 'Sub-division (complaint routing)',
        type: 'select', loader: loadSubDivisions,
        optionLabel: (s) => (s.division_name
          ? `${s.name} (${s.division_name})`
          : s.name) },

      { name: 'city',         label: 'City',       type: 'text' },
      { name: 'state',        label: 'State',      type: 'text' },
      { name: 'address',      label: 'Address',    type: 'textarea' },


      // ----- FLAGS ---------------------------------------------------
      { name: 'is_active',         label: 'Active',           type: 'checkbox', default: true },
      { name: 'is_admin',          label: 'Org admin',        type: 'checkbox',
        hideIf: (user) => isSuperOnPublicSchema(user) },
      { name: 'is_admin_approved', label: 'Admin approved',   type: 'checkbox' },
      { name: 'is_complaints_admin', label: 'Complaints admin', type: 'checkbox' },

      // Password is only meaningful at create-time.  For existing
      // users the dedicated "Change password" button on the edit
      // form calls ``setPassword`` below (POST …/users/:id/set-password/).
      { name: 'password', label: 'Password (only when creating)', type: 'password',
        onlyOnCreate: true, required: true },
    ],
    setPassword: SuperAdminAPI.setUserPassword,
  },

  divisions: {

    slug: 'divisions',
    label: 'Divisions',
    singular: 'Division',
    icon: '🗂️',
    api: BusinessAPI.divisions,
    columns: [...NAME_COLUMNS, OWNER_COLUMN, { key: 'is_active', label: 'Active', format: 'bool' }],
    fields: [
      { name: 'name',       label: 'Name', type: 'text', required: true },
      { name: 'owner',      label: 'Owner', type: 'select', loader: loadUsers, optionLabel: (u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`.trim() },
      { name: 'is_active',  label: 'Active', type: 'checkbox', default: true },
    ],
  },

  // ----- Sub-divisions -------------------------------------------------
  // The second level of the complaint-routing tree: a Division ("Auretics")
  // holds many SubDivisions ("Customer Care", "Billing").  Without a CRUD
  // screen here the ``sub_division`` picker on the Users form would have
  // nothing to choose from, so routing would stay stuck at division level.
  'sub-divisions': {
    slug: 'sub-divisions',
    label: 'Sub-Divisions',
    singular: 'Sub-Division',
    icon: '🗃️',
    api: BusinessAPI.subDivisions,
    columns: [
      { key: 'id',            label: 'ID' },
      { key: 'name',          label: 'Name' },
      // Serializer-provided read-only mirror of the FK, so the list
      // shows "Auretics" instead of a bare id the admin can't read.
      { key: 'division_name', label: 'Division' },
      { key: 'owner_email',   label: 'Owner' },
      { key: 'is_active',     label: 'Active', format: 'bool' },
    ],
    fields: [
      { name: 'name',     label: 'Name', type: 'text', required: true },
      // Required: a sub-division with no parent can never be reached by
      // the routing lookup, so the form refuses to create an orphan.
      { name: 'division', label: 'Division', type: 'select', required: true,
        loader: loadDivisions, optionLabel: (d) => d.name },
      { name: 'owner',    label: 'Owner', type: 'select', loader: loadUsers,
        optionLabel: (u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`.trim() },
      { name: 'is_active', label: 'Active', type: 'checkbox', default: true },
    ],
  },

  departments: {
    slug: 'departments',
    label: 'Departments',

    singular: 'Department',
    icon: '🏬',
    api: BusinessAPI.departments,
    columns: [...NAME_COLUMNS, OWNER_COLUMN],
    fields: [
      { name: 'name',  label: 'Name', type: 'text', required: true },
      { name: 'owner', label: 'Owner', type: 'select', loader: loadUsers,
        optionLabel: (u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`.trim() },
    ],
  },

  depots: {
    slug: 'depots',
    label: 'Depots',
    singular: 'Depot',
    icon: '🏭',
    api: BusinessAPI.depots,
    columns: [...NAME_COLUMNS, OWNER_COLUMN],
    fields: [
      { name: 'name',  label: 'Name', type: 'text', required: true },
      { name: 'owner', label: 'Owner', type: 'select', loader: loadUsers,
        optionLabel: (u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`.trim() },
    ],
  },

  products: {
    slug: 'products',
    label: 'Products',
    singular: 'Product',
    icon: '📦',
    api: BusinessAPI.products,
    columns: [...NAME_COLUMNS, OWNER_COLUMN],
    fields: [
      { name: 'name',  label: 'Name', type: 'text', required: true },
      { name: 'owner', label: 'Owner', type: 'select', loader: loadUsers,
        optionLabel: (u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`.trim() },
    ],
  },

  projects: {
    slug: 'projects',
    label: 'Projects',
    singular: 'Project',
    icon: '🚧',
    api: BusinessAPI.projects,
    columns: [
      { key: 'id',         label: 'ID' },
      { key: 'name',       label: 'Name' },
      { key: 'status',     label: 'Status' },
      { key: 'start_date', label: 'Start' },
      { key: 'end_date',   label: 'End' },
    ],
    fields: [
      { name: 'name',       label: 'Name', type: 'text', required: true },
      { name: 'start_date', label: 'Start date', type: 'date' },
      { name: 'end_date',   label: 'End date',   type: 'date' },
      { name: 'status',     label: 'Status', type: 'select', options: [
        { value: 'in_progress', label: 'In progress' },
        { value: 'completed',   label: 'Completed' },
      ]},
      { name: 'users',      label: 'Members', type: 'multiselect', loader: loadUsers,
        optionLabel: (u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`.trim() },
    ],
  },

  groups: {
    slug: 'groups',
    label: 'Groups',
    singular: 'Group',
    icon: '👥',
    api: BusinessAPI.groups,
    columns: [
      { key: 'id',         label: 'ID' },
      { key: 'name',       label: 'Name' },
      { key: 'created_on', label: 'Created on', format: 'date' },
    ],
    fields: [
      { name: 'name',  label: 'Name', type: 'text', required: true },
      { name: 'users', label: 'Members', type: 'multiselect', loader: loadUsers,
        optionLabel: (u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`.trim() },
    ],
  },

  'hr-managements': {
    slug: 'hr-managements',
    label: 'HR Management',
    singular: 'HR Setting',
    icon: '🧑‍💼',
    api: BusinessAPI.hrManagements,
    columns: [
      { key: 'id',             label: 'ID' },
      { key: 'working_hours',  label: 'Working hours' },
      { key: 'working_days',   label: 'Working days' },
      { key: 'holidays',       label: 'Holidays' },
    ],
    fields: [
      { name: 'working_hours', label: 'Working hours', type: 'number' },
      { name: 'working_days',  label: 'Working days',  type: 'number' },
      { name: 'holidays',      label: 'Holidays',      type: 'number' },
    ],
  },

  'notice-templates': {
    slug: 'notice-templates',
    label: 'Notice Templates',
    singular: 'Notice Template',
    icon: '📨',
    api: BusinessAPI.noticeTemplates,
    columns: [
      { key: 'id',             label: 'ID' },
      { key: 'template_name',  label: 'Name' },
      { key: 'template_event', label: 'Event' },
      { key: 'template_id',    label: 'Template ID' },
    ],
    fields: [
      { name: 'template_name',  label: 'Name', type: 'text', required: true },
      { name: 'template_event', label: 'Event', type: 'text' },
      { name: 'template_id',    label: 'External Template ID', type: 'text' },
      { name: 'sms_sender_id',  label: 'SMS Sender', type: 'text' },
      { name: 'message_template', label: 'SMS message template', type: 'textarea' },
      { name: 'email_sender_id',  label: 'Email sender', type: 'text' },
      { name: 'email_subject',    label: 'Email subject', type: 'text' },
      { name: 'email_text',       label: 'Email body', type: 'textarea' },
      { name: 'remarks',          label: 'Remarks', type: 'text' },
    ],
  },

  'public-complaint-admins': {
    slug: 'public-complaint-admins',
    label: 'Public Complaint Admins',
    singular: 'Public Complaint Admin',
    icon: '🪪',
    api: BusinessAPI.publicComplaintAdmins,
    columns: [
      { key: 'id',                          label: 'ID' },
      { key: 'public_division_name',        label: 'Division' },
      { key: 'public_complaint_admin_email',label: 'Admin' },
      { key: 'is_active',                   label: 'Active', format: 'bool' },
    ],
    fields: [
      { name: 'public_division', label: 'Division', type: 'select', required: true,
        loader: loadDivisions, optionLabel: (d) => d.name },
      { name: 'public_complaint_admin', label: 'Admin user', type: 'select', required: true,
        loader: loadUsers, optionLabel: (u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`.trim() },
      { name: 'is_active', label: 'Active', type: 'checkbox', default: true },
    ],
  },
};

/** Ordered list — used to drive the home page tiles + tabs.
 *
 *  ``users`` is intentionally first: the very first thing an org admin
 *  needs to do on a brand-new tenant is invite people, so the tile is
 *  surfaced as the most prominent action on the Business landing
 *  page.  Everything else (divisions, departments, …) only becomes
 *  useful once there are users to assign them to.
 */
export const BUSINESS_RESOURCE_ORDER = [
  'users',
  'divisions', 'sub-divisions', 'departments', 'depots', 'products',

  'projects',  'groups',      'hr-managements',
  'notice-templates', 'public-complaint-admins',
];

/**
 * Non-CRUD Business pages surfaced as dashboard tiles.
 *
 * These routes are registered as static pages in ``App.jsx`` (not via
 * ``BusinessResource``) but still appear in the top-bar tabs in
 * ``config.js``.  Each entry mirrors a tab so the overview grid stays
 * in sync with navigation.
 */
export const BUSINESS_STATIC_TILES = [
  {
    key: 'app-branding',
    label: 'App branding',
    icon: '🏷️',
    to: '/business/app-branding',
    subtitle: 'Name and logo after sign-in',
  },
  {
    key: 'access-control',
    label: 'Access Control',
    icon: '🔐',
    to: '/business/access-control',
    subtitle: 'Module permissions',
  },
  {
    key: 'defaults',
    label: 'Default',
    icon: '🛠️',
    to: '/business/defaults',
    subtitle: 'System defaults',
  },
  {
    key: 'google-sheets',
    label: 'Google Sheets',
    icon: '📊',
    to: '/business/integrations/google-sheets',
    subtitle: 'Integration settings',
  },
  {
    key: 'public-complaints',
    label: 'Public Complaints',
    icon: '📣',
    to: '/business/public-complaints',
    subtitle: 'Public login at /complaints',
  },
  {
    key: 'complaint-settings',
    label: 'Complaint Settings',
    icon: '🎫',
    to: '/business/complaint-settings',
    subtitle: 'Serial numbering',
  },
  {
    key: 'timezone',
    label: 'Timezone',
    icon: '🌐',
    to: '/business/timezone',
    subtitle: 'Organisation timezone',
  },
  {
    key: 'schema-backup',
    label: 'Backup',
    icon: '💾',
    to: '/business/schema-backup',
    subtitle: "This organisation's schema",
  },
];

